import React, { FunctionComponent, useState } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Collapse,
  List,
} from "@mui/material";
import { Link, matchPath, useLocation } from "react-router-dom";

import { EsgArrowDown, EsgDropLine } from "assets";
import { useGetConfigParameter } from "hooks";
import { isFeatureEnabled } from "utils";

import { ExpandableMenuOptionsProps, MenuTypes, MenuOption } from "./types";
import {
  RESOURCES_MENU_OPTIONS,
  DOCUMENTS_MENU_OPTIONS,
  TARGETS_MENU_OPTIONS,
  SIDEBAR_DIMENSIONS,
} from "./consts";
import {
  listItemStyles,
  listItemButtonStyles,
  iconContainerStyles,
  listItemIconStyles,
  listItemTextStyles,
  arrowContainerStyles,
  sidebarTitleStyles,
  sidebarTitleTextStyles,
  modalStyles,
  modalItemTextStyles,
  dropLineStyles,
  collapseItemStyles,
  collapseItemIconStyles,
  collapseItemTextStyles,
} from "./styles";

/**
 * Reusable expandable menu options component that handles:
 * - Collapsible sidebar behavior
 * - Modal popup when sidebar is closed
 * - Route matching and selection
 * - Consistent styling across different menu types
 * - Auto-generates menu options based on title if not provided
 */
export const ExpandableMenuOptions: FunctionComponent<
  ExpandableMenuOptionsProps
> = ({
  isOpen,
  icon,
  isSelected,
  title,
  options,
  className = "sideBar-main-div",
  // onClick
}) => {
  // Auto-generate options based on title if not provided
  const getMenuOptions = (): MenuOption[] => {
    if (options) return options;

    switch (title) {
      case MenuTypes.RESOURCES:
        return RESOURCES_MENU_OPTIONS;
      case MenuTypes.DOCUMENTS:
        return DOCUMENTS_MENU_OPTIONS;
      case MenuTypes.ACCELERATE:
        return TARGETS_MENU_OPTIONS;
      default:
        return DOCUMENTS_MENU_OPTIONS;
    }
  };

  const { data: configParameter } = useGetConfigParameter();
  const menuOptions = getMenuOptions().filter(
    (option) =>
      !option.configParameter ||
      isFeatureEnabled(configParameter, option.configParameter),
  );
  const { pathname } = useLocation();

  // Find selected option based on current path
  const selectedOption = menuOptions.filter((item) =>
    matchPath(item.pattern || item.to, pathname),
  );

  const initialTabIndex = selectedOption?.length
    ? selectedOption[0]?.key
    : null;

  const [open, setOpen] = useState(initialTabIndex !== null);
  const [selectedIndex, setSelectedIndex] = useState(initialTabIndex);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  // Update selected index when route changes
  if (selectedIndex !== initialTabIndex) {
    setSelectedIndex(initialTabIndex);
  }

  const handleListItemClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    index: number,
  ) => {
    setSelectedIndex(index);
  };

  const handleMouseEnter = (event: any) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setModalPosition({
      top: rect.top,
      left: rect.right - SIDEBAR_DIMENSIONS.MODAL_OFFSET,
    });
    setModalOpen(true);
  };

  const handleMouseLeave = () => {
    setModalOpen(false);
  };

  return (
    <Box
      data-testid="expandable-menu-container"
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: "block",
        width: "100%",
        color: "inherit",
        textDecoration: "none",
      }}
    >
      <ListItem sx={listItemStyles(isOpen)}>
        <ListItemButton
          selected={isSelected}
          onClick={() => setOpen(!open)}
          sx={listItemButtonStyles(isOpen, isSelected)}
        >
          <Box sx={iconContainerStyles(isOpen)}>
            <ListItemIcon sx={listItemIconStyles(isOpen)}>{icon}</ListItemIcon>
            <ListItemText primary={title} sx={listItemTextStyles(isOpen)} />
            {isOpen && (
              <Box sx={arrowContainerStyles}>
                <EsgArrowDown
                  style={{
                    transform: open ? "rotate(0deg)" : "rotate(180deg)",
                    transition: "transform .1s ease-in",
                  }}
                />
              </Box>
            )}
          </Box>

          {!isOpen && (
            <Box sx={sidebarTitleStyles}>
              <Box sx={sidebarTitleTextStyles}>{title}</Box>
            </Box>
          )}
        </ListItemButton>

        {/* Modal popup when sidebar is closed */}
        {!isOpen && isModalOpen && (
          <div
            data-testid="expandable-menu-modal"
            className="sideBar-main-modal"
            style={{
              top: modalPosition.top,
              left: modalPosition.left,
            }}
          >
            {menuOptions.map((item, index) => (
              <Box sx={{ display: "flex", width: "100%" }} key={item.key}>
                <ListItemButton
                  onClick={(event) => {
                    handleListItemClick(event, index);
                    handleMouseLeave();
                    // onClick?.(item.label);
                  }}
                  selected={item.key === selectedIndex}
                  component={Link}
                  to={item.to}
                  sx={{ ...modalStyles, width: "100%", px: 1.5, gap: 1 }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "32px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {item?.icon}
                  </ListItemIcon>
                  <ListItemText sx={modalItemTextStyles} primary={item.label} />
                </ListItemButton>
              </Box>
            ))}
          </div>
        )}
      </ListItem>

      {/* Collapsible menu when sidebar is open */}
      {isOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            sx={{
              paddingLeft: isOpen ? "20px" : "0px",
            }}
          >
            {menuOptions.map((item, index) => (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                key={item.key}
              >
                {isOpen && (
                  <ListItemIcon sx={dropLineStyles}>
                    <EsgDropLine />
                  </ListItemIcon>
                )}
                <ListItemButton
                  onClick={(event) => {
                    handleListItemClick(event, index);
                    // onClick?.(item.label);
                  }}
                  selected={item.key === selectedIndex}
                  component={Link}
                  to={item.to}
                  sx={collapseItemStyles(isOpen)}
                  key={item.label}
                >
                  {isOpen && (
                    <ListItemIcon sx={collapseItemIconStyles(isOpen)}>
                      {item?.icon}
                    </ListItemIcon>
                  )}
                  {isOpen && (
                    <ListItemText
                      sx={collapseItemTextStyles}
                      primary={item.label}
                    />
                  )}
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};
